import { useEffect } from 'react';
import { useCallStore } from '@/stores/call-store';
import { useAuthStore } from '@/stores/auth-store';
import { getSocket, disconnectSocket } from '@/services/socket';

interface IncomingCallData {
  callId: string;
  from: string;
  fromName?: string;
  callType: 'audio' | 'video';
  conversationId?: string;
}

/**
 * Mounted inside AuthGate so any authed user, on any route, can hear the
 * incoming-call ring. Opens a socket on login, tears it down on logout.
 * Pushes to `call-store` which triggers the full-screen CallScreen overlay.
 */
export function IncomingCallGuard() {
  const status = useAuthStore((s) => s.status);
  const incoming = useCallStore((s) => s.incoming);

  useEffect(() => {
    if (status !== 'authed') return;

    let cancelled = false;
    let off: (() => void) | undefined;

    void getSocket().then((socket) => {
      if (cancelled) return;

      const onIncoming = (d: IncomingCallData) => {
        incoming({
          callId: d.callId,
          kind: d.callType,
          remoteUserId: d.from,
          remoteUserName: d.fromName ?? 'Unknown',
        });
      };

      socket.on('call:incoming', onIncoming);
      off = () => socket.off('call:incoming', onIncoming);
    });

    return () => {
      cancelled = true;
      off?.();
    };
  }, [status, incoming]);

  // Disconnect the socket on sign-out so the next login re-auths fresh.
  useEffect(() => {
    if (status === 'guest') disconnectSocket();
  }, [status]);

  return null;
}
